import { createMongoAbility, subject, type MongoAbility, type RawRuleOf } from '@casl/ability';
import { unpackRules, type PackRule } from '@casl/ability/extra';

export type AppAbility = MongoAbility;

export type AbilityScope = {
  restaurantId: string | null;
  franchiseId: string | null;
};

export type ClientAbility =
  | { status: 'no-context' }
  | { status: 'ungated' }
  | { status: 'ready'; ability: AppAbility };

type PackedRule = readonly [string, string, ...unknown[]];

export function buildAbility(rules: readonly PackedRule[]): AppAbility {
  return createMongoAbility(unpackRules(rules as PackRule<RawRuleOf<AppAbility>>[]));
}

export function resolveAbility(
  context: { rules?: readonly PackedRule[] } | null | undefined,
): ClientAbility {
  if (!context) return { status: 'no-context' };
  if (!context.rules) return { status: 'ungated' };
  return { status: 'ready', ability: buildAbility(context.rules) };
}

export type CaslAction = 'create' | 'read' | 'update' | 'delete' | 'manage';

export type CaslSubject =
  | 'Reservation'
  | 'Restaurant'
  | 'Franchise'
  | 'Customer'
  | 'ReservationConfig'
  | 'Zone'
  | 'RestaurantTable'
  | 'Shift'
  | 'FloorPlan'
  | 'OpeningHours'
  | 'Menu'
  | 'Category'
  | 'Product'
  | 'ProductOption'
  | 'OptionChoice'
  | 'PriceList'
  | 'PriceListEntry'
  | 'Order'
  | 'OnlineOrder'
  | 'Payment'
  | 'DeliveryZone'
  | 'OrderShift'
  | 'OrderTypeConfig'
  | 'ManualPaymentMethod'
  | 'PaymentProviderConfig'
  | 'PaymentMethodNote'
  | 'Promotion'
  | 'LoyaltyConfig'
  | 'LoyaltyAccount'
  | 'GiftCard'
  | 'TableSession'
  | 'WaitlistEntry'
  | 'TableOrderingConfig'
  | 'Printer'
  | 'PrintJob'
  | 'OrderLabel'
  | 'SettingChangeLog'
  | 'Worker'
  | 'AiThread'
  | 'PurgeRun'
  | 'PurgeRule'
  | 'PartnerGroup'
  | 'PartnerMember'
  | 'PartnerFranchiseAssignment'
  | 'all';

export function canInScope(
  clientAbility: ClientAbility,
  action: CaslAction,
  subjectType: CaslSubject,
  scope: AbilityScope,
): boolean {
  if (clientAbility.status === 'ungated') return true;
  if (clientAbility.status === 'no-context') return false;
  if (!scope.restaurantId || !scope.franchiseId) return false;
  return clientAbility.ability.can(
    action,
    subject(subjectType, { restaurantId: scope.restaurantId, franchiseId: scope.franchiseId }),
  );
}
